/**
 * A stream element plays a creator's Twitch channel while they are live and their most recent
 * YouTube video otherwise. The page keeps only the two channel addresses; which one plays is
 * decided when the page is served, from the providers' public signals (no API keys).
 */

const twitchHosts = new Set(["twitch.tv", "www.twitch.tv", "m.twitch.tv"]);
const twitchLogin = /^[A-Za-z0-9_]{3,25}$/;
// Twitch's own pages, which share the channel address shape and are never channels.
const twitchPages = new Set([
  "directory",
  "downloads",
  "drops",
  "inventory",
  "jobs",
  "messages",
  "p",
  "prime",
  "search",
  "settings",
  "store",
  "subscriptions",
  "turbo",
  "videos",
  "wallet",
]);
const youtubeHosts = new Set(["youtube.com", "www.youtube.com", "m.youtube.com"]);
const youtubeChannel = /^UC[A-Za-z0-9_-]{22}$/;
const youtubeVideo = /^[A-Za-z0-9_-]{11}$/;

function httpsUrl(value: string) {
  // oxlint-disable-next-line eslint/no-control-regex -- Reject control characters in authored links.
  if (value.length > 1000 || /[\u0000-\u001f\u007f\\]/.test(value)) return null;
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" && !url.username && !url.password && !url.port ? url : null;
  } catch {
    return null;
  }
}

/** The channel a Twitch address names ("https://twitch.tv/komari" → "komari"), or null. */
export function twitchChannelLogin(value: string) {
  const url = httpsUrl(value);
  if (!url || !twitchHosts.has(url.hostname)) return null;
  const parts = url.pathname.split("/").filter(Boolean);
  const login = parts.length === 1 ? parts[0]!.toLowerCase() : "";
  return twitchLogin.test(login) && !twitchPages.has(login) ? login : null;
}

export function twitchChannelUrl(login: string) {
  return `https://www.twitch.tv/${login}`;
}

/** The channel ID in a YouTube channel address (youtube.com/channel/UC…), or null. */
export function youtubeChannelId(value: string) {
  const url = httpsUrl(value);
  if (!url || !youtubeHosts.has(url.hostname)) return null;
  const id = /^\/channel\/([^/]+)\/?$/.exec(url.pathname)?.[1];
  return id && youtubeChannel.test(id) ? id : null;
}

export function youtubeChannelUrl(id: string) {
  return `https://www.youtube.com/channel/${id}`;
}

/**
 * A YouTube address that names a channel some other way (an @handle, /c/ or /user/ name), for the
 * server to look up once while the page is edited. Videos and other pages are not channels.
 */
export function youtubeChannelPage(value: string) {
  const url = httpsUrl(value);
  if (!url || !youtubeHosts.has(url.hostname)) return null;
  const path =
    /^\/(@[\w.-]{3,100})(?:\/[a-z]*)?\/?$/.exec(url.pathname)?.[1] ??
    /^\/((?:c|user)\/[\w.-]{1,100})\/?$/.exec(url.pathname)?.[1];
  return path ? `https://www.youtube.com/${path}` : null;
}

/** The channel a YouTube channel page says it is, from its canonical address. */
export function youtubeChannelFromPage(html: string) {
  return (
    /<link rel="canonical" href="https:\/\/www\.youtube\.com\/channel\/(UC[A-Za-z0-9_-]{22})"/.exec(
      html,
    )?.[1] ??
    /"externalId":"(UC[A-Za-z0-9_-]{22})"/.exec(html)?.[1] ??
    null
  );
}

/**
 * The feed of a channel's long-form videos: the Videos tab, without Shorts or past live streams.
 * Its playlist shares the channel's ID after the "UC" prefix.
 */
export function youtubeVideosFeedUrl(channelId: string) {
  return `https://www.youtube.com/feeds/videos.xml?playlist_id=UULF${channelId.slice(2)}`;
}

/** Every upload, for a channel whose Videos tab is empty. */
export function youtubeChannelFeedUrl(channelId: string) {
  return `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;
}

export type LatestVideo = { id: string; title: string; published: string; short: boolean };

/**
 * A channel's long-form uploads as a playlist YouTube's player can embed: it opens on the newest
 * video. Pages fall back to it when the newest video can't be read on the server (YouTube's feeds
 * have answered 404 for every channel, and it sends servers' page requests to a bot check), since
 * the visitor's browser loads the player itself.
 */
export function youtubeUploadsPlaylistUrl(channelId: string) {
  return `https://www.youtube.com/playlist?list=UULF${channelId.slice(2)}`;
}

const entities: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};
function decodeXml(value: string) {
  return value.replace(/&(#x[0-9a-f]{1,6}|#[0-9]{1,7}|[a-z]{2,4});/gi, (match, name: string) => {
    if (name[0] !== "#") return entities[name.toLowerCase()] ?? match;
    const code = name[1] === "x" ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
  });
}

/** The newest video in a YouTube Atom feed, preferring a regular video over a Short. */
export function latestFeedVideo(xml: string): LatestVideo | null {
  const videos = [...xml.matchAll(/<entry>([\s\S]*?)<\/entry>/g)].flatMap(([, entry]) => {
    const id = /<yt:videoId>([^<]+)<\/yt:videoId>/.exec(entry!)?.[1];
    if (!id || !youtubeVideo.test(id)) return [];
    const title = /<title>([^<]*)<\/title>/.exec(entry!)?.[1] ?? "";
    const published = /<published>([^<]+)<\/published>/.exec(entry!)?.[1] ?? "";
    const short = /<link rel="alternate" href="https:\/\/www\.youtube\.com\/shorts\//.test(entry!);
    return [{ id, title: decodeXml(title).trim().slice(0, 200), published, short }];
  });
  videos.sort((a, b) => b.published.localeCompare(a.published));
  return videos.find((video) => !video.short) ?? videos[0] ?? null;
}

export function youtubeWatchUrl(id: string) {
  return `https://www.youtube.com/watch?v=${id}`;
}

/** What a stream element shows right now; the page renders it as a video it can play. */
export type PageStreamState =
  | { status: "live"; url: string; channel: string }
  | { status: "video"; url: string; title: string; published: string; channel?: string }
  | { status: "offline"; url: string; channel: string };
